// This file is part of Indico.
// Copyright (C) 2002 - 2023 CERN
//
// Indico is free software; you can redistribute it and/or
// modify it under the terms of the MIT License; see the
// LICENSE file for more details.

import privacyNoticeURL from 'indico-url:events.display_privacy';

import _ from 'lodash';
import {nanoid} from 'nanoid';
import PropTypes from 'prop-types';
import React, {useCallback, useState} from 'react';
import {Form as FinalForm, FormSpy} from 'react-final-form';
import {useSelector} from 'react-redux';
import {Button, Form, Message, Modal} from 'semantic-ui-react';

import {Captcha} from 'indico/react/components';
import {
  FinalSubmitButton,
  handleSubmitError,
  FinalCheckbox,
  getChangedValues,
} from 'indico/react/forms';
import {Param, Translate} from 'indico/react/i18n';
import {indicoAxios} from 'indico/utils/axios';
import {getPluginObjects, renderPluginComponents} from 'indico/utils/plugins';

import ConsentToPublishDropdown from '../components/ConsentToPublishDropdown';
import FormSection from '../form/FormSection';
import {getNestedSections, getStaticData, getCurrency} from '../form/selectors';

import RegistrationSummary from './RegistrationSummary';
import {
  getUpdateMode,
  getModeration,
  getManagement,
  getPublishToParticipants,
  getPublishToPublic,
} from './selectors';

import '../../styles/regform.module.scss';

function PrivacyPolicy({url}) {
  const label = (
    <label>
      <Translate>
        I have read and agree to the{' '}
        <Param name="url" wrapper={<a href={url} target="_blank" rel="noreferrer" />}>
          Privacy policy
        </Param>
      </Translate>
    </label>
  );

  return (
    <Message info style={{marginTop: 25}}>
      <Message.Header>
        <Translate>Privacy policy</Translate>
      </Message.Header>
      <Form as="div" style={{marginTop: 10}}>
        <FinalCheckbox required label={label} name="agreed_to_privacy_policy" toggle />
      </Form>
    </Message>
  );
}

PrivacyPolicy.propTypes = {
  url: PropTypes.string.isRequired,
};

function ManagementOptions({isUpdateMode}) {
  return (
    <Message info style={{marginTop: 25}}>
      <Message.Header>
        <Translate>Management Options</Translate>
      </Message.Header>
      <div>
        <FinalCheckbox
          style={{marginTop: 10}}
          label={Translate.string('Send an email notification to the user')}
          name="notify_user"
          toggle
        />
        {!isUpdateMode && (
          <FinalCheckbox
            style={{marginTop: 10}}
            label={Translate.string('Ignore field requirements for custom fields')}
            name="override_required"
            toggle
          />
        )}
      </div>
    </Message>
  );
}
ManagementOptions.propTypes = {
  isUpdateMode: PropTypes.bool.isRequired,
};

function ConsentToPublish({publishToParticipants, publishToPublic}) {
  const isWarning = publishToParticipants === 'show_all' && publishToPublic === 'show_all';
  return (
    <Message info={!isWarning} warning={isWarning} style={{marginTop: 25}}>
      <Message.Header>
        <Translate>Participant list</Translate>
      </Message.Header>
      {(publishToParticipants === 'show_with_consent' ||
        publishToPublic === 'show_with_consent') && (
        <p>
          <Translate>
            Specify whether you consent to being included in the event's list of participants
          </Translate>
        </p>
      )}
      <Form as="div">
        <ConsentToPublishDropdown
          name="consent_to_publish"
          publishToParticipants={publishToParticipants}
          publishToPublic={publishToPublic}
          useFinalForms
        />
      </Form>
    </Message>
  );
}

ConsentToPublish.propTypes = {
  publishToParticipants: PropTypes.oneOf(['hide_all', 'show_with_consent', 'show_all']).isRequired,
  publishToPublic: PropTypes.oneOf(['hide_all', 'show_with_consent', 'show_all']).isRequired,
};

// TEMA: added indicator above submit button to indicate errors
export function RegistrationFormStateIndicator() {
  return (
    <FormSpy
      subscription={{hasSubmitErrors: true, hasValidationErrors: true, dirtySinceLastSubmit: true}}
    >
      {({dirtySinceLastSubmit, hasSubmitErrors, hasValidationErrors}) => (
        <div>
          {hasSubmitErrors && !dirtySinceLastSubmit && (
            <div styleName="form-error">
              <Translate>
                Some data is invalid. Please check all from fields above for issues.
              </Translate>
            </div>
          )}
          {hasValidationErrors && (
            <div styleName="form-error">
              <Translate>Please check all from fields above for issues.</Translate>
            </div>
          )}
        </div>
      )}
    </FormSpy>
  );
}
// TEMA END: added indicator above submit button to indicate errors

export default function RegistrationFormSubmission() {
  const sections = useSelector(getNestedSections);
  const {
    eventId,
    submitUrl,
    // TEMA: Update wording for registration button
    submitLabel,
    // TEMA END: Update wording for registration button
    // TEMA: Add ability to show a summary before booking
    showSummary,
    // TEMA END: Add ability to show a summary before booking
    registrationData,
    initialValues,
    captchaRequired,
    captchaSettings,
    policyAgreementRequired,
  } = useSelector(getStaticData);
  const isUpdateMode = useSelector(getUpdateMode);
  const isModerated = useSelector(getModeration);
  const isManagement = useSelector(getManagement);
  const publishToParticipants = useSelector(getPublishToParticipants);
  const publishToPublic = useSelector(getPublishToPublic);
  const showConsentToPublish = !isManagement && publishToParticipants !== 'hide_all';
  // TEMA: Added ability to conditionally display fields in a section
  const currency = useSelector(getCurrency);
  // TEMA END: Added ability to conditionally display fields in a section

  const [summaryVisible, setSummaryVisible] = useState(false);
  const handleModalClose = useCallback(() => {
    setSummaryVisible(false);
  }, [setSummaryVisible]);

  const onSubmit = async (data, form) => {
    // There is no need to store whether the user accepted the privacy policy
    // as it is not possible to submit the form without accepting it.
    data = _.omit(data, 'agreed_to_privacy_policy');

    // TEMA: Added ability to conditionally display fields in a section
    // Remove data of hidden fields in conditional sections
    if (sections && Array.isArray(sections) && sections.length > 0) {
      for (const section of sections) {
        if (section.isConditional === true) {
          const filteredItems = section.items.filter(item => item.isEnabled);
          if (filteredItems.length > 1) {
            // check if condition is met
            if (data[filteredItems[0].htmlName] !== true) {
              // purge all non required fields
              filteredItems.shift();
              data = _.omit(data, filteredItems.map(({htmlName}) => htmlName));
            }
          }
        }
      }
    }
    // TEMA END: Added ability to conditionally display fields in a section

    // TEMA: Add Debugging for Google Analytics tracking
    try {
      if (localStorage.getItem('debug') === 'true') {
        console.log('--- sections ---');
        console.log(JSON.stringify(sections));

        console.log('--- data ---');
        console.log(JSON.stringify(data));

        // gather all data
        if (sections && Array.isArray(sections) && sections.length > 0) {
          for (const section of sections) {
            const filteredItems = section.items.filter(item => item.isEnabled);
            if (filteredItems.length > 0) {
              // iterate over all items
              for (const item of filteredItems) {
                if (data[item.htmlName] && item.inputType) {
                  switch (item.inputType) {
                    case 'single_choice':
                      if (typeof data[item.htmlName] === 'object') {
                        for (const key of Object.keys(data[item.htmlName])) {
                          for (const choice of item.choices) {
                            if (choice.id === key && choice.price && choice.price > 0) {
                              console.log(`--- tracking item: ${item.htmlName} ---`);
                              console.log(JSON.stringify(choice));
                              console.log(JSON.stringify(data[item.htmlName]));
                            }
                          }
                        }
                      }
                      break;
                    case 'additional_participants':
                      if (Array.isArray(data[item.htmlName])) {
                        for (const participant of data[item.htmlName]) {
                          for (const key of Object.keys(participant.ticket)) {
                            for (const choice of item.choicesTicket) {
                              if (choice.id === key && choice.price && choice.price > 0) {
                                console.log(`--- tracking item: ${item.htmlName} ---`);
                                console.log(JSON.stringify(choice));
                                console.log(JSON.stringify(data[item.htmlName]));
                              }
                            }
                          }
                        }
                      }
                      break;
                    default:
                      console.log(`--- item not tracked: ${item.htmlName} ---`);
                      console.log(JSON.stringify(item));
                      console.log(JSON.stringify(data[item.htmlName]));
                      break;
                  }
                } else {
                  console.log(`--- no data for item ---`);
                  console.log(JSON.stringify(item));
                }
              }
            } else {
              console.log(`--- filtered items have a length of 0 ---`);
            }
          }
        }
      }
    } catch (e) {
      console.log(e);
    }
    // TEMA END: Add Debugging for Google Analytics tracking

    let resp;
    const formData = isUpdateMode ? getChangedValues(data, form, ['notify_user']) : data;
    try {
      resp = await indicoAxios.post(submitUrl, formData);
    } catch (err) {
      return handleSubmitError(err);
    }

    // TEMA: Add Google Analytics tracking
    // Get uuid of the registration
    let trackingId = '';
    try {
      const paramString = resp.data.redirect.split('?')[1];
      const queryString = new URLSearchParams(paramString);
      for (const pair of queryString.entries()) {
        if (pair[0] === 'token') {
          trackingId = pair[1];
        }
      }
    } catch {
      trackingId = nanoid();
    }
    // TEMA END: Add Google Analytics tracking

    if (resp.data.redirect) {
      // TEMA: Add Google Analytics tracking
      try {
        if (gtag !== undefined && typeof gtag === 'function') {
          // gather all data
          const trackingItems = [];
          if (sections && Array.isArray(sections) && sections.length > 0) {
            for (const section of sections) {
              const filteredItems = section.items.filter(item => item.isEnabled);
              if (filteredItems.length > 0) {
                // iterate over all items
                for (const item of filteredItems) {
                  if (data[item.htmlName] && item.inputType) {
                    switch (item.inputType) {
                      case 'single_choice':
                        if (typeof data[item.htmlName] === 'object') {
                          for (const key of Object.keys(data[item.htmlName])) {
                            for (const choice of item.choices) {
                              if (choice.id === key && choice.price && choice.price > 0) {
                                trackingItems.push({
                                  item_id: choice.id,
                                  item_name: choice.caption,
                                  price: choice.price,
                                  quantity: data[item.htmlName][key],
                                });
                              }
                            }
                          }
                        }
                        break;
                      case 'additional_participants':
                        if (Array.isArray(data[item.htmlName])) {
                          for (const participant of data[item.htmlName]) {
                            for (const key of Object.keys(participant.ticket)) {
                              for (const choice of item.choicesTicket) {
                                if (choice.id === key && choice.price && choice.price > 0) {
                                  trackingItems.push({
                                    item_id: choice.id,
                                    item_name: choice.caption,
                                    price: choice.price,
                                    quantity: participant.ticket[key],
                                  });
                                }
                              }
                            }
                          }
                        }
                        break;
                      default:
                        break;
                    }
                  }
                }
              }
            }
          }

          // create object
          const trackingData = {
            transaction_id: trackingId,
            value: trackingItems.reduce(
              (partialSum, itm) => partialSum + itm.price * itm.quantity,
              0
            ),
            currency,
            items: trackingItems,
          };

          gtag('event', 'purchase', trackingData);
          console.log('Purchase tracked.');
          console.log(trackingData);
          for (const itm of trackingItems) {
            console.log(itm);
          }
        } else {
          console.log('Purchase not tracked. Google Analytics is not available.');
        }
      } catch (err) {
        console.log('Purchase not tracked. Google Analytics is not available.');
        console.log(err);
      }
      // TEMA END: Add Google Analytics tracking

      // redirect
      location.href = resp.data.redirect;
      // never finish submitting to avoid fields being re-enabled
      await new Promise(() => {});
    }
  };

  return (
    <FinalForm
      onSubmit={onSubmit}
      initialValues={isUpdateMode ? registrationData : initialValues}
      initialValuesEqual={_.isEqual}
      subscription={{}}
      decorators={getPluginObjects('regformFormDecorators')}
    >
      {fprops => (
        <form onSubmit={fprops.handleSubmit}>
          {sections.map(section => (
            <FormSection key={section.id} {...section} />
          ))}
          {renderPluginComponents('regformAfterSections')}
          <div>
            {isManagement && <ManagementOptions isUpdateMode={isUpdateMode} />}
            {showConsentToPublish && (
              <ConsentToPublish
                publishToParticipants={publishToParticipants}
                publishToPublic={publishToPublic}
              />
            )}
            {captchaRequired && <Captcha settings={captchaSettings} />}
            {!isManagement && !isUpdateMode && policyAgreementRequired && (
              <PrivacyPolicy url={privacyNoticeURL({event_id: eventId})} />
            )}
            {/* TEMA: added indicator above submit button to indicate errors */}
            <RegistrationFormStateIndicator />
            {/* TEMA END: added indicator above submit button to indicate errors */}
            {isUpdateMode || showSummary === 'false' ? (
              <FinalSubmitButton
                disabledUntilChange={false}
                disabledIfInvalid={false}
                // TEMA: Update wording for registration button
                label={
                  isUpdateMode
                    ? Translate.string('Modify')
                    : submitLabel
                    ? submitLabel
                    : Translate.string('Register now')
                }
                // TEMA End: Update wording for registration button
                style={{
                  marginTop: 20,
                  textAlign: 'right',
                }}
              />
            ) : (
              <FormSpy subscription={{hasValidationErrors: true}}>
                {({hasValidationErrors}) => (
                  <div className="d-flex flex-row justify-content-end">
                    <Button
                      type="button"
                      disabled={hasValidationErrors}
                      onClick={e => {
                        e.stopPropagation();
                        setSummaryVisible(true);
                      }}
                      content={showSummary}
                      style={{
                        marginTop: 20,
                        textAlign: 'right',
                      }}
                    />
                  </div>
                )}
              </FormSpy>
            )}
          </div>
          <Modal open={summaryVisible} size="large" onClose={handleModalClose}>
            <Modal.Header>
              <Translate>Summary</Translate>
            </Modal.Header>
            <RegistrationSummary />
            <Modal.Actions className="d-flex flex-row justify-content-end">
              <Button content={Translate.string('Edit data')} onClick={handleModalClose} />
              <FinalSubmitButton
                disabledUntilChange={false}
                disabledIfInvalid={false}
                onClick={fprops.handleSubmit}
                label={
                  isUpdateMode
                    ? Translate.string('Modify')
                    : submitLabel
                    ? submitLabel
                    : Translate.string('Register now')
                }
              />
            </Modal.Actions>
          </Modal>
        </form>
      )}
    </FinalForm>
  );
}
